import { Button, Icon } from "@aidkitorg/component-library";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { Dropdown } from "./Components/Dropdown";
import { Translations } from "@aidkitorg/i18n/lib";
import { TableKey, fields } from "./SupportUtil";
import { ColumnDef } from "@tanstack/react-table";

type SupportTableV2Props = {
  allColumns: ColumnDef<any>[];
  rows: (JSX.Element | null)[];
  handleSort: (key: TableKey, switchOrder: boolean) => void;
  sortKey: string;
  sortOrder: "asc" | "desc";
  targetFieldFilters: Record<string, string>;
  setTargetFieldFilters: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
  targetFieldFilterOptions: Record<string, string[]>;
  L: Translations;
  channel: string;
};

export function SupportTableV2({
  allColumns,
  rows,
  handleSort,
  sortKey,
  sortOrder,
  targetFieldFilters,
  setTargetFieldFilters,
  targetFieldFilterOptions,
  L,
  channel
}: SupportTableV2Props) {
  const buttonsColumn = { kind: 'field', key: '', label: '', sortable: false, filterable: false };
  // Transform ColumnDef to Column format
  const v2Columns = allColumns.map(col => {
    const accessorKey = (col as any).accessorKey as string;
    const field = fields.find(f => f.key === accessorKey);
    return field ? {
      kind: 'field',
      key: field.key as string,
      sortable: field.sortable
    } : {
      kind: 'extraColumn',
      key: accessorKey,
      label: (col as any).header as string,
      filterable: true
    };
  });

  return (
    <table className="block table-auto max-w-fit divide-y divide-gray-300 overflow-x-auto" style={{maxHeight: '70vh'}}>
      <thead className="bg-gray-50 sticky top-0">
        <tr>
          {[buttonsColumn, ...v2Columns].map((column) => {
            return <th scope="col" key={column.key}
              className="py-2.5 pl-3 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
              {column.kind === 'field' ? <div className="flex space-x-2 justify-between items-center h-10">
                <div className="">
                  {L.support.fields[column.key as TableKey]}
                </div>
                {column.sortable && (
                  <div className="flex-col">
                    {sortKey !== column.key ?
                      <Button
                        icon={<Icon
                          name="ChevronRightIcon"
                          role="decorative"
                        />}
                        size="sm"
                        onClick={() => {
                          handleSort(column.key as TableKey, false);
                        }}
                        variant="unstyled"
                        aria-label="Sort items by column"
                      /> :
                      <Button
                        icon={<Icon
                          name={sortOrder === 'asc' ? "ChevronUpIcon" : "ChevronDownIcon"}
                          role="decorative"
                        />}
                        size="sm"
                        aria-label="Sort items by column"
                        onClick={() => {
                          handleSort(column.key as TableKey, true);
                        }}
                        variant="unstyled"
                      /> 
                    }
                  </div>)}
              </div> : <div className="flex space-x-2 justify-between items-center h-10">
                <div className="">
                  {column.label}
                </div>
                {column.filterable && (
                  <div className="flex items-center space-x-2">
                    <Dropdown
                      usePopper={true}
                      label={targetFieldFilters[column.key] || <FunnelIcon className="cursor-pointer h-4 w-4 text-gray-400" />}
                      options={[
                        { label: L.admin.survey_design.clear, callback: () => { setTargetFieldFilters((prev) => ({ ...prev, [column.key]: '' })) } },
                        ...(Array.isArray(targetFieldFilterOptions[column.key]) ? targetFieldFilterOptions[column.key] : []).map((option) => ({
                          label: option,
                          callback: () => {
                            setTargetFieldFilters((prev) => ({ ...prev, [column.key]: option }));
                          }
                        }))]
                      }
                    />
                  </div>
                )}
              </div>
              }
            </th>
          })}
          <th scope="col" className="flex-1 py-3.5 pl-3 pr-4 sm:pr-6">
            <span className="sr-only">Open Case</span>
          </th>
        </tr>
      </thead>
      <tbody id="support-tbody" className="divide-y divide-gray-200 bg-white">
        {rows.filter(r => r).map(r => r)}
      </tbody>
    </table>
  )
}
